import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogPortal,
  DialogTitle,
} from '../../components/ui/dialog';
import AuthFirstSalesPage from './AuthFirstSalesPage';
import './AuthDialogChrome.css';

type AuthFirstDialogProps = {
  analyticsContext?: Record<string, unknown>;
  authSourceOverride?: string;
  entryMode?: 'discovery' | 'checkout' | 'login' | 'signup';
  forceFreshLogin?: boolean;
  newSignupReturnTo?: string;
  onAuthenticated?: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  returnTo: string;
};

/**
 * Keep password-manager / browser-extension UI interactive.
 *
 * Radix modal dialogs set `pointer-events: none` on `document.body` and only
 * re-enable events on the dialog layer. Keeper, 1Password, and similar
 * extensions inject their popups as body siblings, so they become visible but
 * unclickable (X does nothing). Auth login must stay non-modal for that reason.
 */
const preventDismissFromExtensionUi = (event: Event) => {
  event.preventDefault();
};

const AuthFirstDialog = ({
  analyticsContext,
  authSourceOverride,
  entryMode = 'discovery',
  forceFreshLogin = false,
  newSignupReturnTo,
  onAuthenticated,
  onOpenChange,
  open,
  returnTo,
}: AuthFirstDialogProps): React.ReactElement => {
  const contentRef = useRef<HTMLDivElement>(null);
  const title =
    entryMode === 'checkout'
      ? 'Create or sign in to continue to checkout'
      : entryMode === 'login'
        ? 'Sign in to BotSpot'
        : 'Create your BotSpot account';
  const description =
    entryMode === 'checkout'
      ? 'We’ll sign you in, or create an account. Your plan is saved.'
      : entryMode === 'login'
        ? 'Sign in, or create an account if you’re new.'
        : 'We’ll create an account, or sign you in if one exists.';

  return (
    <Dialog modal={false} onOpenChange={onOpenChange} open={open}>
      <DialogPortal>
        <div
          aria-hidden="true"
          className="bsui-overlay auth-first-dialog-backdrop"
          data-state={open ? 'open' : 'closed'}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) {
              onOpenChange(false);
            }
          }}
        />
      </DialogPortal>
      <DialogContent
        className={`auth-first-dialog-content auth-first-dialog-content--clarity${entryMode === 'login' || entryMode === 'signup' ? ' auth-first-dialog-content--auth-only' : ''}`}
        closeButtonClassName="auth-first-close-control"
        onFocusOutside={preventDismissFromExtensionUi}
        onInteractOutside={preventDismissFromExtensionUi}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          contentRef.current?.focus({ preventScroll: true });
        }}
        onPointerDownOutside={preventDismissFromExtensionUi}
        ref={contentRef}
        tabIndex={-1}
      >
        <DialogTitle className="bsui-sr-only">{title}</DialogTitle>
        <DialogDescription className="bsui-sr-only">{description}</DialogDescription>
        <AuthFirstSalesPage
          analyticsContext={analyticsContext}
          authSourceOverride={authSourceOverride}
          entryMode={entryMode}
          forceFreshLogin={forceFreshLogin}
          hideAuthHeaderFromAssistiveTech
          newSignupReturnTo={newSignupReturnTo}
          onAuthenticated={onAuthenticated}
          presentation="overlay"
          returnTo={returnTo}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AuthFirstDialog;
